import React from "react";
import { Loader } from "@/components/ui/loader";
import type { Evidence } from "@/types/evidences";
import { BusinessReportOutcomeHeader } from "./businessReportOutcomeHeader";
import { BusinessReportOutcomeInit } from "./businessReportOutcomeInit";
import { BusinessReportOutcomeExport } from "./export";
import { BusinessReportOutcomeWebsocket } from "./businessReportOutcomeWebsocket";
import { BusinessReportOutcomeRow } from "./businessReportOutcomeRow";
import { useGetBusinessReportOutcomeQuery } from "../businessReports.api";
import { BusinessReportOutcomeSaveToCollection } from "@/modules/snapshots/outcome-details/saveToCollection";
import { BusinessReportOutcomeVerify } from "@/modules/snapshots/outcome-details/verify";
import * as analytics from "@/services/analytics";

interface Props {
  outcomeId: string;
}

export interface PromptResult {
  evidences: Evidence[];
  content: string;
  show: boolean;
  processing: boolean;
  name: string;
  prompt: string;
  order: number;
  business_report_outcome_result_id: string;
  business_report_prompt_id: string;
}

export const BusinessReportOutcomeContent: React.FC<Props> = ({ outcomeId }) => {
  const { data: businessReportOutcome = null } = useGetBusinessReportOutcomeQuery(outcomeId);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [isComplete, setIsComplete] = React.useState(false);
  const [userScrolled, setUserScrolled] = React.useState(false);

  const [promptsResult, setPromptsResult] = React.useState<PromptResult[]>([]);

  React.useEffect(() => {
    setIsComplete(businessReportOutcome?.status === "COMPLETED");
  }, [businessReportOutcome?.status]);

  React.useEffect(() => {
    if (businessReportOutcome?.name) {
      analytics.snapshotView(businessReportOutcome.name);
    }
  }, [businessReportOutcome?.name]);

  const handleNewMessage = React.useCallback(() => {
    if (wrapperRef.current !== null && isComplete && !userScrolled) {
      wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight;
    }
  }, [isComplete, userScrolled]);

  const inProgress = businessReportOutcome?.status === "NEW" || businessReportOutcome?.status === "IN_PROGRESS";

  const onScroll = React.useCallback(() => {
    if (wrapperRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = wrapperRef.current;
      setUserScrolled(scrollTop + clientHeight < scrollHeight - 50);
    }
  }, []);

  const onPromptTypewriterEnded = React.useCallback(
    (prompt: PromptResult) => {
      const index = promptsResult.findIndex(row => row.business_report_prompt_id === prompt.business_report_prompt_id);

      const nextPrompt = promptsResult[index + 1];
      if (nextPrompt) {
        setPromptsResult(prev =>
          prev.map(row => ({
            ...row,
            processing: row.business_report_prompt_id === nextPrompt.business_report_prompt_id,
            show: row.business_report_prompt_id === nextPrompt.business_report_prompt_id ? true : row.show,
          })),
        );
      }
    },
    [setPromptsResult, promptsResult],
  );

  return (
    <div className="flex flex-grow flex-col overflow-hidden p-6 pt-0">
      <div className="mb-6 flex items-center justify-between">
        <BusinessReportOutcomeHeader businessReportOutcome={businessReportOutcome} />
        <BusinessReportOutcomeInit
          businessReportOutcome={businessReportOutcome}
          setPromptsResult={setPromptsResult}
          inProgress={inProgress}
        />
        <div className="flex justify-end">
          {" "}
          {isComplete && businessReportOutcome && (
            <BusinessReportOutcomeVerify businessReportOutcome={businessReportOutcome} />
          )}
          {isComplete && businessReportOutcome && (
            <BusinessReportOutcomeExport businessReportOutcomeId={businessReportOutcome.id} />
          )}
          {isComplete && businessReportOutcome && !businessReportOutcome?.snapshot_collection_id && (
            <BusinessReportOutcomeSaveToCollection businessReportOutcome={businessReportOutcome} />
          )}
        </div>
      </div>
      <div
        className="h-full overflow-scroll overflow-x-hidden rounded-lg bg-white p-6 shadow"
        ref={wrapperRef}
        onScroll={onScroll}
      >
        {businessReportOutcome && (
          <BusinessReportOutcomeWebsocket
            setIsComplete={setIsComplete}
            businessReportOutcome={businessReportOutcome}
            setPromptsResult={setPromptsResult}
          />
        )}
        {businessReportOutcome &&
          promptsResult
            .filter(row => row.show && row.content)
            .map(promptResult => (
              <BusinessReportOutcomeRow
                businessReportOutcomeId={businessReportOutcome.id}
                businessReportOutcomeResultId={promptResult.business_report_outcome_result_id}
                onScroll={handleNewMessage}
                onPromptTypewriterEnded={onPromptTypewriterEnded}
                evidences={promptResult.processing ? [] : promptResult.evidences}
                content={promptResult.content}
                promptResult={promptResult}
                processing={promptResult.processing}
                key={promptResult.business_report_prompt_id}
              />
            ))}

        {!isComplete && <Loader />}
        {businessReportOutcome?.status === "FAILED" && <p className="text-center">Report processing failed.</p>}
      </div>
    </div>
  );
};
